import { StepsStep } from '@utils/enums/StepsStep.enum';
import { StepContainer } from './Steps.styles';
import MultiplePatientsTabs from '@components/MultiplePatietnsTabs/MultiplePatientsTabs';
import { useCallback, useMemo } from 'react';
import {
  LuxTypography,
  LuxSelectablePill,
  Stack,
  useTheme,
} from '@gemini/fe-ds-core';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { useTranslation } from 'react-i18next';

interface RouteContainerProps {
  children: React.ReactNode;
  focusRef?: React.RefObject<HTMLDivElement>;
  noStyle?: boolean;
  showTitle?: boolean;
  showMultiplePatientsTabs?: boolean;
  customTitleKeyLabel?: string;
}
const RouteContainer = ({
  children,
  focusRef,
  noStyle = false,
  showTitle = true,
  showMultiplePatientsTabs = true,
  customTitleKeyLabel,
}: RouteContainerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const currentStep = useOnlineBookingStore(state => state.currentFormStep);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const infoUser = useOnlineBookingStore(state => state.infoUser);
  const showAddNewPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const showAddPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const hasCustomPage = useMemo(() => {
    return formConfigs?.customPage?.show;
  }, [formConfigs]);

  const getCardTitle = useCallback(() => {
    if (
      showAddPatientFrame &&
      ((currentStep === StepsStep.CUSTOM_PAGE && hasCustomPage) ||
        (currentStep === StepsStep.PATIENT_DETAILS && !hasCustomPage))
    ) {
      return t('newPatientPage.title');
    }
    switch (currentStep) {
      case StepsStep.APPOINTMENT_TYPE:
        return t('appointmentTypePage.title');
      case StepsStep.INSURANCE:
        return t('insurancePage.title');
      case StepsStep.PATIENT_DETAILS:
        return t('patientDetailsPage.title');
      case StepsStep.CUSTOM_PAGE:
        return t('customPage.title');
      case StepsStep.CONTACT_DETAILS:
        return t('contactDetailsPage.title');
      default:
        return null;
    }
  }, [currentStep, showAddPatientFrame, hasCustomPage, t]);
  return (
    <Stack gap={'1rem'}>
      {currentStep === StepsStep.PATIENT_DETAILS &&
        !showAddNewPatientFrame &&
        infoUser &&
        //if email is present it means we are logged in from wcs side. Otherwise, show this pill
        !infoUser.email && (
          <LuxSelectablePill
            variant="pill"
            color="info"
            selectable={false}
            text={
              <LuxTypography
                fontSize={theme.palette.LUX.obTokens.textSmSize}
                fontFamily={theme.palette.LUX.obTokens.textSmFamily}
                style={{ whiteSpace: 'normal' }}
              >
                {t('patientDetailsPage.signInToFill')}
              </LuxTypography>
            }
          />
        )}
      <StepContainer
        ref={focusRef}
        tabIndex={-1}
        noStyle={noStyle}
        borderColor={theme.palette.LUX.obTokens.bordersColorNeutralSecondary}
      >
        {showTitle && (
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4">
              {customTitleKeyLabel ? t(customTitleKeyLabel) : getCardTitle()}
            </LuxTypography>
          </CardTitleWrapper>
        )}
        {showMultiplePatientsTabs && <MultiplePatientsTabs />}
        {children}
      </StepContainer>
    </Stack>
  );
};

export default RouteContainer;
